import { UserRole } from '~/types/account'
import { aiData, getCookieValue } from '~/utils/auth'
import { identify } from '~/utils/clarity'
import type { DeferredAppInsights } from '~/utils/deferredAppInsights'

// What nuxt2's auth plugin did on every page load (plugins/auth.ts:282-309), and
// nuxt4 did only inside login/logout, so a reload lost all three:
// - arm the session-expiry timer: the expiry dialog for a signed-in user, the
//   quiet anonymous-session renewal (cartReplace(true)) for everyone else;
// - tag the Clarity session with the App Insights user and session ids;
// - identify the signed-in user to App Insights.
//
// Runs on app:mounted: once per page load, after the app has mounted with its
// auth state and persisted values hydrated. The .client suffix is what keeps it
// off the server. AB#64457
export const onPageLoad = (nuxtApp: { $appInsights: Pick<DeferredAppInsights, 'setAuthenticatedUserContext' | 'onLoaded'> }) => {
  const auth = useSAuth()
  const role = auth.user.value?.role

  // nuxt2's condition: authenticated, an agent, or anonymous. A logged-out
  // session still carrying a customer role gets no timer, as in nuxt2.
  if (auth.isAuthenticated.value || role === UserRole.Agent || (role ?? UserRole.Anonymous) === UserRole.Anonymous) {
    auth.createTimeout()
  }

  // The App Insights SDK starts after hydration and writes ai_user then, so a first
  // visit has no ai_user at mount. ai_session follows the SDK's first item, nearly
  // always the queued first page view (see utils/deferredAppInsights.ts). Without a
  // connection string (local dev) the SDK never writes them, and the visitor is not
  // identified.
  nuxtApp.$appInsights.onLoaded(() => identify(getCookieValue('ai_user'), aiData(auth.user.value?.email)))

  if (auth.isAuthenticated.value && auth.user.value?.email) {
    nuxtApp.$appInsights.setAuthenticatedUserContext(auth.user.value.email)
  }
}

export default defineNuxtPlugin({
  name: 'auth-session',
  dependsOn: ['persistent'],
  setup (nuxtApp) {
    nuxtApp.hook('app:mounted', () => nuxtApp.runWithContext(() => onPageLoad(nuxtApp)))
  }
})
